import ReactHtmlParser from 'react-html-parser';
import React, { useState, useRef, useEffect } from 'react';
import Button from 'react-bootstrap/lib/Button';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { defaultMessages } from '../../../../libs/i18n/default';
import {
  redirectToLogin,
  checkForExistingHmsUser,
  checkForExistingFirebaseUser,
  postCreateAuthenticatedUser,
  isRetryableCreateAuthenticatedUserFailure,
  resolveCreateAuthenticatedUserFailureMessage,
  createAuthenticatedUserRetryLabel,
  isCreateAuthenticatedUserTerminalFailure,
  markEmailVerificationPending
} from '../../../../shared/lou/auth'
import { setAuthenticatedUser } from '../../../../shared/actions';
import GoogleAnalyticsFields from '../../../../shared/components/GoogleAnalyticsFields';

const FirebaseCreateAccountForm = ({ intl: {formatMessage}, setEmailVerified }) => {
  const dispatch = useDispatch()
  const territory = useSelector(state => state.territory)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailValid, setEmailValid] = useState(null)
  const [passwordValid, setPasswordValid] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [userExists, setUserExists] = useState(null)
  const [checkedSignInMethods, setCheckedSignInMethods] = useState(false)
  const [gaClientId, setGaClientId] = useState('')
  const [gaSessionId, setGaSessionId] = useState('')
  const [submitError, setSubmitError] = useState(null)
  const emailInputRef = useRef(null);
  const timer = useRef()

  const createAuthenticatedUser = async (e) => {
    setSubmitError(null)

    try {
      const response = await postCreateAuthenticatedUser(email, password, 'host', gaClientId, gaSessionId)
      const data = await response.json()

      if (data.success) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'signup_success',
          userType: 'host'
        });

        dispatch(setAuthenticatedUser(data.authenticated_user));
        
        if (!data.authenticated_user.email_verified) {
          markEmailVerificationPending('host')
          setEmailVerified(false)
        }
        return
      }

      const retryable = isRetryableCreateAuthenticatedUserFailure(response.status, data.error_code)
      const message = resolveCreateAuthenticatedUserFailureMessage({
        status: response.status,
        errorCode: data.error_code,
        message: data.message
      })

      setSubmitting(false)
      setSubmitError({ message, retryable, errorCode: data.error_code })
    } catch (_error) {
      setSubmitting(false)
      setSubmitError({
        message: resolveCreateAuthenticatedUserFailureMessage({ status: 503 }),
        retryable: true
      })
    }
  }

  const showRetryLabel = (error) => {
    if (!error?.retryable) return false

    return !/please\s+(try\s+again|retry)/i.test(error.message || '')
  }

  const submitText = () => {
    if (!checkedSignInMethods) { return defaultMessages.clientCommonButtonsContinue }

    return defaultMessages.clientHostPagesRegisterButtonsSignup
  }

  const submit = async (e) => {
    e.preventDefault()

    setSubmitting(true)

    if (!checkedSignInMethods) {
      const hmsUserExists = await checkForExistingHmsUser(territory, email)
      const firebaseUserExists = await checkForExistingFirebaseUser(territory, email);

      if (hmsUserExists || firebaseUserExists) {
        setUserExists(true)
        redirectToLogin(territory, email)
      } else {
        setCheckedSignInMethods(true)
        setSubmitError(null)
        setSubmitting(false)
      }
    } else if (checkedSignInMethods) {
      await createAuthenticatedUser(e)
    }
  }

  const buttonDisabled = () => {
    if (!checkedSignInMethods) return !emailValid

    if (submitError && isCreateAuthenticatedUserTerminalFailure(submitError.errorCode)) return true

    return !(emailValid && passwordValid && !userExists)
  }

  const inputsDisabled = submitError && isCreateAuthenticatedUserTerminalFailure(submitError.errorCode)

  useEffect(() => {
    if(!email) return

    if (timer.current != null) {
      clearTimeout(timer.current)
    }

    timer.current = setTimeout(() => {
      const isValid = emailInputRef.current.checkValidity();
      setEmailValid(isValid)

    }, 500)
  }, [email])

  useEffect(() => {
    if(!password) return

    if (timer.current != null) {
      clearTimeout(timer.current)
    }

    timer.current = setTimeout(() => {
      fetch(`/users/validate_password`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ password })
      }).then(response => response.json())
        .then(data => {
          setPasswordValid(data.valid)
        }
      );
    }, 500)
  }, [password])

  return (
    <div className="register_form">
      <form onSubmit={(e) => submit(e)}>
        <div className="text-center mt-3 smaller">
          {ReactHtmlParser(formatMessage(defaultMessages.clientCommonSignupPromptHtml))}
        </div>
        <input
          type="email"
          id="email"
          required
          className="form-control"
          ref={emailInputRef}
          style={{marginBottom: '5px', marginTop: '10px'}}
          placeholder={formatMessage(defaultMessages.clientHostPagesRegisterPlaceholdersEmail)}
          pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
          onChange={e => setEmail(e.target.value)}
          disabled={inputsDisabled}
        />
        {emailValid === false && (
          <div>
            <p style={{ color: 'red', fontSize: 'smaller' }}>
              {formatMessage(defaultMessages.clientGuestPagesRegisterPopoversEmailInvalid)}
            </p>
          </div>
        )}
        {checkedSignInMethods && (
          <>
            <input
              type="password"
              id="password"
              required
              className="form-control"
              style={{marginBottom: '5px', marginTop: '10px'}}
              placeholder={formatMessage(defaultMessages.clientHostPagesRegisterPlaceholdersPassword)}
              minLength="8"
              onChange={e => setPassword(e.target.value)}
              disabled={inputsDisabled}
            />
            <div>
              <p style={{ color: passwordValid === false ? 'red' : 'black', fontSize: 'smaller' }}>
                {formatMessage(defaultMessages.clientCommonPasswordRequirements)}
              </p>
            </div>
          </>
        )}

        <GoogleAnalyticsFields
          gaClientId={gaClientId}
          gaSessionId={gaSessionId}
          setGaClientId={setGaClientId}
          setGaSessionId={setGaSessionId}
        />

        <div className="text-center">
          {submitError && (
            <div style={{ marginTop: 10 }} role="alert">
              <p style={{ color: '#b77600', fontSize: 'smaller' }}>
                {submitError.message}
              </p>
              {showRetryLabel(submitError) && (
                <p style={{ fontSize: 'smaller' }}>
                  {createAuthenticatedUserRetryLabel()}
                </p>
              )}
            </div>
          )}
          <Button
            type="submit"
            id='submit_btn'
            disabled={buttonDisabled()}
            className="btn btn-primary btn-lg"
            style={{marginTop: '10px'}}
          >
            {submitting ? <i className="fa fa-fw fa-spinner fa-spin" /> : formatMessage(submitText())}
          </Button>
          {userExists && (
            <div style={{ marginTop: 10 }}>
              <p>
                {formatMessage(defaultMessages.clientFirebaseEmailInUse)}
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

export default injectIntl(FirebaseCreateAccountForm);
